import React from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import DatePicker from '../../Common/DatePicker/DatePicker';
import Toggle from '../../Common/Toggle/Toggle';
import { CsvToIntArray } from '../../../helpers/strings';
import { getMilestoneIds, getDateValue } from '../../../helpers/reports';
import '../Reports.scss';

export class ProjectTasksListFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            projectId: '',
            baselineId: '',
            projectZeroDate: '',
            statusIds: '',
            departmentIds: '',
            milestoneIds: '',
            taskNames: '',
            phaseIds: '',
            communityIds: '',
            targetDateFrom: '',
            targetDateTo: '',
            useReportDateForTargetDateFrom: false,
            useReportDateForTargetDateTo: false
        }

        this.state = {
            emptyForm: initialFormSelections,
            projectId: initialFormSelections.projectId,
            baselineId: initialFormSelections.baselineId,
            projectZeroDate: initialFormSelections.projectZeroDate,
            statusIds: initialFormSelections.statusIds,
            departmentIds: initialFormSelections.departmentIds,
            milestoneIds: initialFormSelections.milestoneIds,
            taskNames: initialFormSelections.taskNames,
            phaseIds: initialFormSelections.phaseIds,
            communityIds: initialFormSelections.communityIds,
            targetDateFrom: initialFormSelections.targetDateFrom,
            targetDateTo: initialFormSelections.targetDateTo,
            useReportDateForTargetDateFrom: initialFormSelections.useReportDateForTargetDateFrom,
            useReportDateForTargetDateTo: initialFormSelections.useReportDateForTargetDateTo
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,
                projectId: appState.projectId ? appState.projectId : newState.projectId,
                baselineId: appState.baselineId ? appState.baselineId : newState.baselineId,
                projectZeroDate: appState.projectZeroDate ? appState.projectZeroDate : newState.projectZeroDate,
                statusIds: appState.statusIds ? appState.statusIds : newState.statusIds,
                departmentIds: appState.departmentIds ? appState.departmentIds : newState.departmentIds,
                milestoneIds: appState.milestoneIds ? appState.milestoneIds : newState.milestoneIds,
                taskNames: appState.taskNames ? appState.taskNames : newState.taskNames,
                phaseIds: appState.phaseIds ? appState.phaseIds : newState.phaseIds,
                communityIds: appState.communityIds ? appState.communityIds : newState.communityIds,
                targetDateFrom: appState.targetDateFrom ? appState.targetDateFrom : newState.targetDateFrom,
                targetDateTo: appState.targetDateTo ? appState.targetDateTo : newState.targetDateTo
            }
        }

        if (this.props.isSchedulingMode) {
            newState.useReportDateForTargetDateFrom = appState.useReportDateForTargetDateFrom ? true : newState.useReportDateForTargetDateFrom;
            newState.useReportDateForTargetDateTo = appState.useReportDateForTargetDateTo ? true : newState.useReportDateForTargetDateTo;
        } else {
            newState.useReportDateForTargetDateFrom = false;
            newState.useReportDateForTargetDateTo = false;
        }

        // Possibly override app state values with default values or URL params
        let projectId = this.getDefaultProjectId(newState.projectId);

        newState = {
            ...newState,
            projectId: projectId
        }

        this.setState({
            ...this.state,
            ...newState
        });

        /**
         * If newState.projectId has a value, then we need to trigger a project
         * change to make sure we immediately load the ProjectZeroDate and 
         * Project Baseline options, because this does not get automatically 
         * triggered just by setting the value in state.
         */
        if (newState.projectId) {
            this.props.onProjectChange(newState.projectId);
        }
    }

    getDefaultProjectId = (savedProjectId) => {
        /**
         * Project IDs(s) can be sent in via the URL params.  If they are set there
         * then we override the default or saved values and select the values from
         * the URL params
         */
        let projectId = '';
        const urlProjectId = this.props.urlParams.projectId;
        if (urlProjectId && urlProjectId.trim().length > 0) {
            projectId = urlProjectId;
        } else if (savedProjectId) {
            projectId = savedProjectId;
        }

        return projectId;
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        if (!this.state.projectId || this.state.projectId <= 0) {
            return false;
        }

        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.projectId = (reportRequest.projectId) ? reportRequest.projectId : form.projectId;
        form.baselineId = (reportRequest.baselineId) ? reportRequest.baselineId : form.baselineId;
        form.projectZeroDate = getDateValue(reportRequest.projectZeroDate);
        form.statusIds = (reportRequest.statusIds) ? reportRequest.statusIds.join(',') : form.statusIds;
        form.departmentIds = (reportRequest.departmentIds) ? reportRequest.departmentIds.join(',') : form.departmentIds;
        form.milestoneIds = getMilestoneIds(reportRequest.isMilestone);
        form.taskNames = (reportRequest.taskNames) ? reportRequest.taskNames.join(',') : form.taskNames;
        form.phaseIds = (reportRequest.phaseIds) ? reportRequest.phaseIds.join(',') : form.phaseIds;
        form.communityIds = (reportRequest.communityIds) ? reportRequest.communityIds.join(',') : form.communityIds;
        form.targetDateFrom = getDateValue(reportRequest.targetDateFrom);
        form.targetDateTo = getDateValue(reportRequest.targetDateTo);
        form.useReportDateForTargetDateFrom = (reportRequest.useReportDateForTargetDateFrom) ? true : form.useReportDateForTargetDateFrom;
        form.useReportDateForTargetDateTo = (reportRequest.useReportDateForTargetDateTo) ? true : form.useReportDateForTargetDateTo;

        this.setState({
            ...this.state,
            ...form
        });

        /** 
         * Make sure to trigger the parent's loading of the proper project dependent
         * filter values
         */
        if (reportRequest.projectId) {
            this.props.onProjectChange(reportRequest.projectId);
        }
    }

    mapToApiRequest = () => {
        let isMilestone = null; // default to none or both
        if (this.state.milestoneIds.length > 0) {
            const selectedMilestoneIds = this.state.milestoneIds.split(',');
            if (selectedMilestoneIds.length == 1) {
                isMilestone = selectedMilestoneIds[0] === '1' ? true : false;
            }
        }

        const request = {
            showMoreFields: false,
            reportId: this.props.report.id,
            projectId: this.state.projectId,
            baselineId: this.state.baselineId,
            projectZeroDate: this.state.projectZeroDate,
            statusIds: CsvToIntArray(this.state.statusIds),
            departmentIds: CsvToIntArray(this.state.departmentIds),
            isMilestone: isMilestone,
            taskNames: this.state.taskNames && this.state.taskNames.trim().length > 0 ? this.state.taskNames.split(',') : [],
            phaseIds: CsvToIntArray(this.state.phaseIds),
            communityIds: CsvToIntArray(this.state.communityIds),
            targetDateFrom: this.state.targetDateFrom,
            targetDateTo: this.state.targetDateTo
        };

        if (this.props.isSchedulingMode) {
            request.useReportDateForTargetDateFrom = this.state.useReportDateForTargetDateFrom;
            request.useReportDateForTargetDateTo = this.state.useReportDateForTargetDateTo;
        }

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({ ...this.state, [name]: value });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onDatePickerChange = (date, name) => {
        this.onFieldChange(name, date);
    }

    onProjectChange = (value, name) => {
        // Save the changed value in our local state, as usual
        this.onFieldChange(name, value);

        // Tell our parent the project value has changed so they can reload the
        // potential values for the other fields
        if (value && value > 0) {
            this.props.onProjectChange(value);
        }
    }

    render() {
        /**
         * Project Dependent Filters don't exist until a project has been selected,
         * so we must ensure we send MultiSelectBox an empty array instead of undefined
         * or it throws errors
         */
        const projectZeroDateOptions = this.props.filters.projectZeroDates ? this.props.filters.projectZeroDates : [];
        const projectBaselineOptions = this.props.filters.projectBaselines ? this.props.filters.projectBaselines : [];
        const projectTaskOptions = this.props.filters.tasks ? this.props.filters.tasks : [];
        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectId"
                            label="Project" idField="id" valueField="name"
                            options={this.props.filters.projects}
                            selectedValues={this.state.projectId.toString()}
                            onChange={this.onProjectChange}
                            isRequired={true}
                            errorMessage="Project is required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="baselineId"
                            label="Baseline" idField="id" valueField="name"
                            options={projectBaselineOptions}
                            selectedValues={this.state.baselineId.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectZeroDate"
                            label="Project Zero Date / Anticipated Completion" idField="id" valueField="name"
                            options={projectZeroDateOptions}
                            selectedValues={this.state.projectZeroDate.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="statusIds"
                            label="Status" idField="id" valueField="name"
                            options={this.props.filters.statuses}
                            selectedValues={this.state.statusIds.toString()}
                            onChange={this.onDropdownChange}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="departmentIds"
                            label="Department" idField="id" valueField="name"
                            options={this.props.filters.departments}
                            selectedValues={this.state.departmentIds.toString()}
                            onChange={this.onDropdownChange}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="milestoneIds"
                            label="Milestone" idField="id" valueField="name"
                            options={this.props.filters.milestones}
                            selectedValues={this.state.milestoneIds.toString()}
                            onChange={this.onDropdownChange}
                            isMulti={true} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="taskNames"
                            label="Task Name" idField="id" valueField="name"
                            options={projectTaskOptions}
                            selectedValues={this.state.taskNames.toString()}
                            onChange={this.onDropdownChange}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="phaseIds"
                            label="Phase" idField="id" valueField="name"
                            options={this.props.filters.phases}
                            selectedValues={this.state.phaseIds.toString()}
                            onChange={this.onDropdownChange}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="communityIds"
                            label="Community" idField="id" valueField="name"
                            options={this.props.filters.communities}
                            selectedValues={this.state.communityIds.toString()}
                            onChange={this.onDropdownChange}
                            isMulti={true} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-2">
                        <DatePicker showIcon name="targetDateFrom"
                            value={this.state.targetDateFrom}
                            label="Target Date From"
                            onChange={this.onDatePickerChange} />
                    </div>
                    <div className="col-sm-12 col-md-2">
                        <DatePicker showIcon name="targetDateTo"
                            value={this.state.targetDateTo}
                            label="To"
                            onChange={this.onDatePickerChange} />
                    </div>
                </div>
                {
                    this.props.isSchedulingMode &&
                    <div className="row">
                        <div className="col-sm-12 col-md-2">
                            <Toggle name="useReportDateForTargetDateFrom"
                                text="Substitute Current Date for Beginning Target Date"
                                checked={this.state.useReportDateForTargetDateFrom}
                                onChange={this.onFieldChange} />
                        </div>
                        <div className="col-sm-12 col-md-2">
                            <Toggle name="useReportDateForTargetDateTo"
                                text="Substitute Current Date for Ending Target Date"
                                checked={this.state.useReportDateForTargetDateTo}
                                onChange={this.onFieldChange} />
                        </div>
                    </div>
                }
            </form>
        );
    }
}

ProjectTasksListFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    registerReportFilterFunctions: PropTypes.func.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    onProjectChange: PropTypes.func.isRequired,
    isSchedulingMode: PropTypes.bool,
};

export default ProjectTasksListFilters;
